import { useState } from 'react';
import { Link } from 'react-router-dom';
import { GripVertical, Info, MessageSquare, UserX } from 'lucide-react';
import type { TenantConfig, TicketListRow, TicketState } from '../lib/api.js';
import { ago, minutes, personLabel } from '../lib/format.js';
import { shortRequestType } from '../lib/labels.js';
import { Avatar, Badge, SlaRing, Tooltip, cn } from '../ui/index.js';
import { legalTransitionsFrom } from '@esa/shared';
import { slaConsumed, slaVerdict } from '../lib/sla.js';
import { TicketContextMenu, type useTicketActions } from './TicketActions.js';

/**
 * Board view.
 *
 * Native HTML5 drag and drop, deliberately: a drag library would add ~30kB
 * and a layout-animation engine to move one card between five columns. This
 * stays instant, and the only visual work during a drag is a class change on
 * the hovered column.
 *
 * Dropping onto a waiting column routes through the same action hook the
 * context menu uses, so it hits the reason prompt and the state machine
 * rather than writing a state directly.
 */

const COLUMNS: { state: TicketState; label: string; hint: string }[] = [
  { state: 'new', label: 'New', hint: 'Nobody has looked yet' },
  { state: 'open', label: 'Working', hint: 'Actively being worked' },
  { state: 'pending_requester', label: 'On requester', hint: 'Waiting for the person who asked' },
  { state: 'pending_external', label: 'On a third party', hint: 'Blocked outside ESA' },
  { state: 'resolved', label: 'Resolved', hint: 'Done, pending auto-close' },
];

const ACCENT: Record<TicketState, string> = {
  new: 'bg-info',
  open: 'bg-brand',
  pending_requester: 'bg-ink-tertiary',
  pending_internal: 'bg-ink-tertiary',
  pending_event: 'bg-ink-tertiary',
  pending_external: 'bg-accent',
  resolved: 'bg-ok',
  closed: 'bg-ink-tertiary',
  cancelled: 'bg-ink-tertiary',
};

export function Board({
  tenant,
  rows,
  config,
  meOid,
  actions,
  onPeek,
}: {
  tenant: string;
  rows: TicketListRow[];
  config: TenantConfig | undefined;
  meOid: string | undefined;
  actions: ReturnType<typeof useTicketActions>;
  onPeek: (n: number) => void;
}) {
  const [dragging, setDragging] = useState<TicketListRow | null>(null);
  const [over, setOver] = useState<TicketState | null>(null);

  return (
    <div className="scroll-x pb-2">
      <div className="flex min-w-max gap-3">
        {COLUMNS.map((col) => {
          const items = rows.filter((r) => r.state === col.state);
          const isTarget = over === col.state;
          // Not a drop target if the move would be refused. The board
          // showed "Drop here" on every column including the illegal ones,
          // so a resolved card offered "Blocked externally" and the only
          // feedback was a rejection after the fact.
          const canDrop =
            dragging !== null &&
            dragging.state !== col.state &&
            legalTransitionsFrom(dragging.state).includes(col.state);

          return (
            <section
              key={col.state}
              onDragOver={(e) => {
                if (!canDrop) return;
                e.preventDefault();
                setOver(col.state);
              }}
              onDragLeave={() => setOver((c) => (c === col.state ? null : c))}
              onDrop={(e) => {
                e.preventDefault();
                setOver(null);
                if (!dragging || dragging.state === col.state) return;
                actions.move(
                  {
                    number: dragging.number,
                    state: dragging.state,
                    priority: dragging.priority,
                    assigneeOid: dragging.assignee_oid ?? null,
                  },
                  col.state,
                );
                setDragging(null);
              }}
              className={cn(
                'flex w-[19.5rem] shrink-0 flex-col rounded-lg transition-colors duration-fast',
                isTarget && canDrop
                  ? 'bg-brand-tint/70 ring-2 ring-inset ring-brand/40'
                  : 'bg-surface-sunken/60',
              )}
            >
              <header className="flex items-center gap-2 px-3.5 pb-2 pt-3">
                <span aria-hidden className={cn('h-2 w-2 rounded-full', ACCENT[col.state])} />
                <h3 className="text-sm font-semibold text-ink">{col.label}</h3>
                <span className="nums rounded-full bg-surface px-1.5 text-2xs font-medium text-ink-secondary shadow-xs">
                  {items.length}
                </span>
                <Tooltip content={col.hint}>
                  <span className="ml-auto cursor-help text-ink-tertiary/60 transition-colors hover:text-ink-tertiary">
                    <Info aria-hidden className="h-3.5 w-3.5" />
                  </span>
                </Tooltip>
              </header>

              <div className="min-h-[8rem] flex-1 space-y-2 px-2.5 pb-2.5">
                {items.length === 0 && (
                  <div
                    className={cn(
                      'dotted flex h-24 items-center justify-center rounded-md text-2xs text-ink-tertiary',
                      canDrop && 'border border-dashed border-brand/40',
                    )}
                  >
                    {canDrop ? 'Drop here' : 'Nothing here'}
                  </div>
                )}

                {items.slice(0, 40).map((t) => {
                  const paused = t.state.startsWith('pending_');
                  return (
                    <TicketContextMenu
                      key={t.id}
                      tenant={tenant}
                      target={{
                        number: t.number,
                        state: t.state,
                        priority: t.priority,
                        assigneeOid: t.assignee_oid ?? null,
                        queueKey: t.queue_key,
                      }}
                      config={config}
                      meOid={meOid}
                      actions={actions}
                      onPeek={() => onPeek(t.number)}
                    >
                      <article
                        draggable
                        onDragStart={(e) => {
                          setDragging(t);
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/plain', String(t.number));
                        }}
                        onDragEnd={() => {
                          setDragging(null);
                          setOver(null);
                        }}
                        onClick={() => onPeek(t.number)}
                        className={cn(
                          'card-soft group cursor-pointer rounded-md p-3 transition-[transform,box-shadow,opacity] duration-fast',
                          'hover:-translate-y-0.5 hover:shadow-md active:cursor-grabbing',
                          dragging?.id === t.id && 'opacity-40',
                        )}
                      >
                        <div className="mb-1.5 flex items-center gap-1.5">
                          <GripVertical
                            aria-hidden
                            className="-ml-1 h-3.5 w-3.5 shrink-0 text-ink-tertiary opacity-0 transition-opacity group-hover:opacity-100"
                          />
                          <Link
                            to={`/t/${tenant}/${t.number}`}
                            onClick={(e) => e.stopPropagation()}
                            className="nums text-2xs font-medium text-brand hover:underline"
                          >
                            #{t.number}
                          </Link>
                          <span className="truncate text-3xs uppercase tracking-wide text-ink-tertiary">
                            {shortRequestType(t.request_type)}
                          </span>
                          <span className="ml-auto shrink-0">
                            {/* The real share of the target, not the flat 0.3
                                this used to draw for every unbreached card —
                                a ring that shows the same thing for a ticket
                                an hour old and one three days late is a
                                decoration pretending to be a measurement. */}
                            <SlaRing
                              size={18}
                              consumed={slaConsumed(t)}
                              breached={t.resolution_breached}
                              paused={paused}
                              verdict={slaVerdict(t)}
                            />
                          </span>
                        </div>

                        <p className="line-clamp-2 text-sm leading-snug text-ink">{t.subject}</p>

                        <div className="mt-2.5 flex items-center gap-2">
                          {t.assignee_email ? (
                            <Tooltip content={personLabel(t.assignee_name, t.assignee_email)}>
                              <span>
                                <Avatar
                                  name={t.assignee_name}
                                  email={t.assignee_email}
                                  size="xs"
                                />
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip content="Unassigned">
                              <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full border border-dashed border-warn/60 bg-warn-tint">
                                <UserX aria-hidden className="h-2.5 w-2.5 text-warn" />
                              </span>
                            </Tooltip>
                          )}

                          <span className="truncate text-2xs text-ink-tertiary">
                            {personLabel(t.requester_name, t.requester_email)}
                          </span>

                          {Number(t.reply_count) > 1 && (
                            <span className="ml-auto flex shrink-0 items-center gap-0.5 text-2xs text-ink-tertiary">
                              <MessageSquare aria-hidden className="h-2.5 w-2.5" />
                              {t.reply_count}
                            </span>
                          )}
                        </div>

                        {t.paused_minutes > 0 && (
                          <p className="mt-2 text-2xs text-accent">
                            blocked {minutes(t.paused_minutes)}
                          </p>
                        )}
                        {t.needs_human_routing && (
                          <span className="mt-2 inline-block">
                            <Badge tone="warn">Needs routing</Badge>
                          </span>
                        )}
                      </article>
                    </TicketContextMenu>
                  );
                })}

                {items.length > 40 && (
                  <p className="px-1 py-2 text-center text-2xs text-ink-tertiary">
                    +{items.length - 40} more — use the list view
                  </p>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
